import { Button, Icons, WaxSeal } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { track } from "../features/analytics/track.ts";
import { useT } from "../features/i18n/index.ts";
import { CONSENT_VERSION, useSession } from "../features/session/session-context.tsx";

export const Route = createFileRoute("/age-gate")({
  component: AgeGateScreen,
});

function AgeGateScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { acceptConsent, consent } = useSession();
  const [blocked, setBlocked] = useState(false);
  const [confirmedAge, setConfirmedAge] = useState(consent.accepted);
  const [confirmedConsent, setConfirmedConsent] = useState(consent.accepted);
  const ready = confirmedAge && confirmedConsent;

  const continueFlow = () => {
    track("age_gate_confirmed", { gate_result: "pass" });
    // ASSUMPTION: this screen combines the age-gate and the privacy/consent
    // toggle into one confirm action (no per-purpose scope picker exists in
    // the UI yet — PRIV-006 granular withdrawal is a future iteration), so
    // `consent_accepted` fires alongside `age_gate_confirmed` with a single
    // "core" scope rather than a real per-purpose enum array.
    track("consent_accepted", { consent_version: CONSENT_VERSION, scopes: ["core"] });
    acceptConsent();
    navigate({ to: "/line-profile" });
  };

  const blockUnderage = () => {
    track("age_gate_confirmed", { gate_result: "fail" });
    setBlocked(true);
  };

  return (
    <div className="relative mx-auto flex min-h-dvh w-full max-w-md flex-col overflow-hidden">
      <div className="jod-ambient" aria-hidden="true" />
      <div className="jod-grain" aria-hidden="true" />

      {/* scrollable content — taller now, so the CTA lives in a sticky footer */}
      <div className="relative z-10 flex-1 overflow-y-auto px-7 pt-10 pb-4">
        <div className="space-y-6">
          <header className="jod-fade-up flex flex-col items-center gap-4 text-center">
            <WaxSeal size={84} />
            <div className="space-y-1.5">
              <p className="jod-kicker">{t("ageGate.kicker")}</p>
              <h1 className="h-display text-[1.7rem] leading-tight">{t("ageGate.title")}</h1>
            </div>
          </header>

          {/* consent toggles — behaviour unchanged */}
          <section className="jod-fade-up glass space-y-3 rounded-xl p-4">
            <ConsentToggle
              checked={confirmedAge}
              onChange={setConfirmedAge}
              label={t("ageGate.confirmAge")}
            />
            <ConsentToggle
              checked={confirmedConsent}
              onChange={setConfirmedConsent}
              label={t("ageGate.confirmConsent", { version: CONSENT_VERSION })}
            />
          </section>
        </div>
      </div>

      {/* sticky action bar — keeps the primary CTA in one-hand reach */}
      <div
        className="relative z-10 border-hairline border-t px-7 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] backdrop-blur-md"
        style={{ background: "color-mix(in oklch, var(--background) 86%, transparent)" }}
      >
        {blocked ? (
          <div className="glass rounded-xl p-4 text-center text-cream-2 text-sm">
            {t("ageGate.blockedNote")}
          </div>
        ) : (
          <div className="space-y-2.5">
            <Button size="lg" className="w-full" disabled={!ready} onClick={continueFlow}>
              {t("ageGate.confirm")}
            </Button>
            <Button size="lg" variant="quiet" className="w-full" onClick={blockUnderage}>
              {t("ageGate.under20")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface ConsentToggleProps {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}

function ConsentToggle({ checked, label, onChange }: ConsentToggleProps) {
  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-start gap-3 rounded-lg border border-hairline bg-[oklch(100%_0_0_/_3%)] px-3 py-3 text-left"
    >
      <span
        className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded border ${
          checked ? "border-gold bg-gold text-night" : "border-hairline text-transparent"
        }`}
      >
        <Icons.IconCheck size={14} />
      </span>
      <span className="text-cream-2 text-sm leading-relaxed">{label}</span>
    </button>
  );
}
